import React, { createContext, useEffect, useState } from "react";

interface ThemeContextType {
  theme: string;
  toggleTheme: () => void;
  hasUserToggled: boolean;
  setUserToggled: () => void;
}

export const ThemeContext = createContext<ThemeContextType>({
  theme: "light",
  toggleTheme: () => {},
  hasUserToggled: false,
  setUserToggled: () => {},
});


const safeGet = (k: string): string | null => {
  try { return sessionStorage.getItem(k); } catch { return null; }
};
const safeSet = (k: string, v: string): void => {
  try { sessionStorage.setItem(k, v); } catch {}
};

const getInitialTheme = (): string => {
  try {
    const storedTheme = sessionStorage.getItem("theme");
    if (storedTheme === "dark" || storedTheme === "light") return storedTheme;

    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    return prefersDark ? "dark" : "light";
  } catch {
    return "light";
  }
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<string>(getInitialTheme);
  const [hasUserToggled, setHasUserToggled] = useState(false);

  useEffect(() => {

    try {
      localStorage.removeItem("theme");
      localStorage.removeItem("userToggled");
    } catch {}

    setHasUserToggled(safeGet("userToggled") === "true");
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("theme-switching");
    root.setAttribute("data-theme", theme);

    safeSet("theme", theme);

    const t = window.setTimeout(() => {
      root.classList.remove("theme-switching");
    }, 350);

    return () => window.clearTimeout(t);
  }, [theme]);

  const toggleTheme = () => setTheme((prev) => (prev === "light" ? "dark" : "light"));

  const setUserToggled = () => {
    setHasUserToggled(true);
    safeSet("userToggled", "true");
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, hasUserToggled, setUserToggled }}>
      {children}
    </ThemeContext.Provider>
  );
};
